import {DocumentTypeVal, PersonLegalRole} from '@modifi/enums'
import {AnyAction, updateFactory} from '@modifi/redux'
import {aPush} from '@modifi/router'
import {
  aUserFeedBackSubmitSuccessful,
  BENEFICIAL_OWNER_COUNTRY,
  BENEFICIAL_OWNER_NATIONALITY,
  BENEFICIAL_OWNER_POLITICALLY_EXPOSED,
  BENEFICIAL_OWNER_POLITICALLY_EXPOSED_REASON,
  BENEFICIAL_OWNER_UBO_OTHER_REASON,
  filterDocumentsByInputFieldName,
} from '@modifi/plugin-onboarding'
import {
  AdminBeneficialOwnerView,
  AdminPatchPersonIdentificationRequestView,
  AdminPatchPersonRequestView,
} from '@modifi/onboarding-api-clients'
import {BENEFICIAL_OWNER_DOCUMENT_INPUT_FIELD_NAMES} from '../constants'
import {getLogger} from '../../../../utils'
import {
  aFetchDocumentsByExternalPersonId,
  selectDocumentsByExternalPersonId,
  aUpdatePersonDocuments,
} from '../../../../data/person-documents'
import {aCreatePerson, aUpdatePerson} from '../../../../modules/persons'
import {
  mapBeneficialOwnerToFormData,
  mapBeneficialOwnerToRequest,
  pickBeneficialOwnerDocumentProperties,
  pickBeneficialOwnerPersonProperties,
  pickBeneficialOwnerRoleProperties,
} from '../utils'
import {aClearDataSourcePerson, selectDataSourcePerson} from './data-source'
import {aLinkPersonToLegalRole, aUpdatePersonToLegalRoleLink} from '../../../../modules/legal-roles'
import {ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS} from '../../../../routes/constants'
import {selectInputFieldNamesBySectionName} from '../../../../modules/input-fields'
import {SECTION_ULTIMATE_BENEFICIAL_OWNERS} from '../../../../constants/sections'
import {AppThunkAction} from '../../../../types/redux'
import {
  aAppendBeneficialOwnerToCompany,
  aFetchBeneficialOwners,
  aUpdateLocalBeneficialOwner,
  selectBeneficialOwnerByExternalId,
  selectBeneficialOwnersWhereLoaded,
} from '../../../../data/beneficial-owners'
import {aFetchCompany, selectCurrentCompaniesCountryCode} from '../../../../data/company'
import {
  aFetchLegalRepresentatives,
  selectLegalRepresentativeByExternalId,
} from '../../../../data/legal-representatives'
import {aFetchInputFields} from '../../../../data/input-fields'
import {aFetchDirectors, selectDirectorByExternalId} from '../../../../data/directors'

const logger = getLogger('beneficial-owners-page')

type DocumentChange = {
  added: {fileId: string; documentType: DocumentTypeVal}[]
  removed: string[]
}

export const selectInitializePagePending = (
  state: Record<string, any>,
  externalPersonId?: string
): boolean => {
  if (externalPersonId) {
    const beneficialOwnersWhereLoaded = selectBeneficialOwnersWhereLoaded(state)
    if (!beneficialOwnersWhereLoaded) {
      return true
    }
  }
  return false
}

export const selectInitialFormDataForCreation = (
  state: Record<string, any>
): {defaultValues: Record<string, any>; initialValues: Record<string, any>} => {
  const inputFieldsToRender = selectInputFieldNamesBySectionName(
    state,
    SECTION_ULTIMATE_BENEFICIAL_OWNERS
  )

  const defaultValues: Record<string, any> = {
    [BENEFICIAL_OWNER_POLITICALLY_EXPOSED_REASON]: '',
    [BENEFICIAL_OWNER_POLITICALLY_EXPOSED]: false,
  }

  const initialValues: Record<string, any> = {
    'dataSource.externalPersonId': '',
    'dataSource.showSelect': false,
  }

  Object.assign(
    initialValues,
    BENEFICIAL_OWNER_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
      // eslint-disable-next-line no-param-reassign
      result[inputName] = []
      return result
    }, {} as Record<string, []>)
  )

  if (inputFieldsToRender.includes(BENEFICIAL_OWNER_NATIONALITY)) {
    defaultValues[BENEFICIAL_OWNER_NATIONALITY] = selectCurrentCompaniesCountryCode(state)
  }
  defaultValues[BENEFICIAL_OWNER_COUNTRY] = selectCurrentCompaniesCountryCode(state)

  const dataSourceExternalPersonId = selectDataSourcePerson(state)

  if (dataSourceExternalPersonId) {
    const documents =
      selectDocumentsByExternalPersonId(
        state,
        dataSourceExternalPersonId
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      )?.map(({fileId, documentType}) => ({fileId, documentType: documentType!})) || []

    Object.assign(
      initialValues,
      BENEFICIAL_OWNER_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
        // eslint-disable-next-line no-param-reassign
        result[inputName] = filterDocumentsByInputFieldName(inputName, documents)
        return result
      }, {} as Record<string, any>)
    )

    const {
      address,
      externalId: _,
      ...rest
    } = selectLegalRepresentativeByExternalId(state, dataSourceExternalPersonId) ||
    selectDirectorByExternalId(state, dataSourceExternalPersonId) ||
    {}

    Object.assign(
      initialValues,
      mapBeneficialOwnerToFormData({
        ...address,
        ...rest,
      })
    )
  }

  if (
    typeof initialValues[BENEFICIAL_OWNER_UBO_OTHER_REASON] === 'undefined' ||
    initialValues[BENEFICIAL_OWNER_UBO_OTHER_REASON].length === 0
  ) {
    initialValues['ultimateBeneficialOwner.uboReason'] = 'company-ownership'
  } else {
    initialValues['ultimateBeneficialOwner.uboReason'] = 'other'
  }

  return {
    initialValues,
    defaultValues,
  }
}

export const selectInitialFormDataForEditing = (
  state: Record<string, any>,
  externalPersonId: string
): {defaultValues: Record<string, any>; initialValues: Record<string, any>} => {
  const defaultValues: Record<string, any> = {
    [BENEFICIAL_OWNER_POLITICALLY_EXPOSED_REASON]: '',
    [BENEFICIAL_OWNER_POLITICALLY_EXPOSED]: false,
  }

  const initialValues: Record<string, any> = {}

  const documents =
    selectDocumentsByExternalPersonId(
      state,
      externalPersonId
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    )?.map(({fileId, documentType}) => ({fileId, documentType: documentType!})) || []

  Object.assign(
    initialValues,
    BENEFICIAL_OWNER_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
      // eslint-disable-next-line no-param-reassign
      result[inputName] = filterDocumentsByInputFieldName(inputName, documents)
      return result
    }, {} as Record<string, any>)
  )

  // externalPersonId is a legal rep
  const {address, ...rest} = selectBeneficialOwnerByExternalId(state, externalPersonId) || {}
  Object.assign(
    initialValues,
    mapBeneficialOwnerToFormData({
      ...address,
      ...rest,
    })
  )

  if (
    typeof initialValues[BENEFICIAL_OWNER_UBO_OTHER_REASON] === 'undefined' ||
    initialValues[BENEFICIAL_OWNER_UBO_OTHER_REASON].length === 0
  ) {
    initialValues['ultimateBeneficialOwner.uboReason'] = 'company-ownership'
  } else {
    initialValues['ultimateBeneficialOwner.uboReason'] = 'other'
  }

  return {
    initialValues,
    defaultValues,
  }
}

export const aInitializePage =
  (externalPersonId?: string): AppThunkAction<Promise<void>> =>
  async (dispatch): Promise<void> => {
    // always there when company was loaded
    // dispatch(aEnsureBeneficialOwners())
    if (externalPersonId) {
      dispatch(aFetchDocumentsByExternalPersonId(externalPersonId))
    }
  }

const {reducer, actions, selectors} = updateFactory({
  rootSelector: state => state?.pages?.beneficialOwners?.form,
  actionNamePrefix: 'pages/beneficial-owners/form',
  submitRequest:
    (a, changes: Record<string, any>, externalPersonId?: string | undefined) =>
    async (dispatch, getState): Promise<void> => {
      logger.info('Submitting beneficial owner page')
      logger.debug('changes: %o', changes)

      const documentChanges = Object.values(pickBeneficialOwnerDocumentProperties(changes))
      const personChangesAsRequestData = mapBeneficialOwnerToRequest(
        pickBeneficialOwnerPersonProperties(changes)
      ) as AdminPatchPersonRequestView
      const roleChangesAsRequestData = mapBeneficialOwnerToRequest(
        pickBeneficialOwnerRoleProperties(changes)
      ) as AdminPatchPersonIdentificationRequestView

      let updatedPerson

      dispatch(a.pendingAction())

      const createdBeneficialOwner: AdminBeneficialOwnerView = {
        externalId: externalPersonId,
      }
      if (!externalPersonId && typeof changes['dataSource.externalPersonId'] === 'string') {
        // eslint-disable-next-line no-param-reassign
        externalPersonId = changes['dataSource.externalPersonId']
        Object.assign(
          createdBeneficialOwner,
          selectLegalRepresentativeByExternalId(getState(), externalPersonId) ||
            selectDirectorByExternalId(getState(), externalPersonId)
        )
      }

      const updateLegalRole =
        !!externalPersonId && !!selectBeneficialOwnerByExternalId(getState(), externalPersonId)

      let existingPersonUpdated = false
      try {
        logger.info('Create or update the person: %o', personChangesAsRequestData)
        if (externalPersonId) {
          if (Object.keys(personChangesAsRequestData).length > 0) {
            existingPersonUpdated = true
            logger.debug('Person already exists - externalId is: %s, updating...', externalPersonId)
            updatedPerson = await dispatch(
              aUpdatePerson(externalPersonId, personChangesAsRequestData) as any
            )
            Object.assign(createdBeneficialOwner, updatedPerson)
          } else {
            logger.debug('No person related changes detected')
          }
        } else {
          logger.debug('Person does not exist yet, creating...')
          updatedPerson = await dispatch(aCreatePerson(personChangesAsRequestData) as any)
          Object.assign(createdBeneficialOwner, updatedPerson)
          // eslint-disable-next-line no-param-reassign
          externalPersonId = updatedPerson.externalId
        }

        logger.info(
          'Creating or updating the person beneficial owner link: %o',
          roleChangesAsRequestData
        )

        if (!externalPersonId) {
          throw new Error(`Unknown externalPersonId`)
        }

        if (updateLegalRole) {
          if (Object.keys(roleChangesAsRequestData).length > 0) {
            logger.debug('Person is already a beneficial owner, updating...')

            Object.assign(createdBeneficialOwner, roleChangesAsRequestData)
            // @todo handle address?
            // @ts-ignore needs cleanup
            dispatch(aUpdateLocalBeneficialOwner(createdBeneficialOwner, true))

            await dispatch(
              aUpdatePersonToLegalRoleLink(
                externalPersonId,
                PersonLegalRole.BENEFICIAL_OWNER,
                roleChangesAsRequestData
              ) as unknown as AnyAction
            )
          } else {
            logger.debug('No role related changes detected')
          }
        } else {
          logger.debug('Person is not yet a beneficial owner, creating...')

          Object.assign(createdBeneficialOwner, roleChangesAsRequestData)

          await dispatch(
            aLinkPersonToLegalRole(
              externalPersonId,
              PersonLegalRole.BENEFICIAL_OWNER,
              roleChangesAsRequestData
            )
          )

          // @ts-ignore cleanup
          dispatch(aAppendBeneficialOwnerToCompany(createdBeneficialOwner))
        }

        logger.info('Linking & unlinking documents to the person: %o', documentChanges)
        if (documentChanges.length > 0) {
          const mergedChanges = documentChanges.reduce(
            (result, change: DocumentChange) => {
              if (change.added && change.added.length > 0) {
                result.added.push(...change.added)
              }
              if (change.removed && change.removed.length > 0) {
                result.removed.push(...change.removed)
              }
              return result
            },
            {added: [], removed: []} as DocumentChange
          )
          await dispatch(aUpdatePersonDocuments(externalPersonId, mergedChanges))
        } else {
          logger.debug('No document related changed detected')
        }
      } catch (error: any) {
        logger.error('Error while creating or updating a person: %o', error)
        dispatch(a.errorAction(error))
        return
      } finally {
        await Promise.all([
          dispatch(aFetchInputFields()),
          dispatch(aFetchCompany()),
          dispatch(aFetchBeneficialOwners()),
        ])
      }

      // if Exisiting person updated is also a director or Legal Rep then fetch directors
      // or Legal Reps to update the their tables respectively in UI
      if (existingPersonUpdated && !!selectDirectorByExternalId(getState(), externalPersonId)) {
        await dispatch(aFetchDirectors())
      }
      if (
        existingPersonUpdated &&
        !!selectLegalRepresentativeByExternalId(getState(), externalPersonId)
      ) {
        await dispatch(aFetchLegalRepresentatives())
      }

      // redirect to newly created beneficial owner
      dispatch(aPush(ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS))

      dispatch(a.successAction())
      dispatch(aUserFeedBackSubmitSuccessful(SECTION_ULTIMATE_BENEFICIAL_OWNERS))
    },

  reset:
    a =>
    (dispatch): void => {
      dispatch(a.clearAction())
      dispatch(aClearDataSourcePerson())
    },
})

export default reducer

export const {
  selectUpdatePending: selectSavePending,

  selectUpdateHasError: selectSaveHasError,
  selectUpdateErrorMessage: selectSaveErrorMessage,
} = selectors

export const {aReset, aSubmit: aSavePage} = actions
